/**
 * Where the labs apps live, relative to wherever the PLM is being served from.
 *
 * The convention: a lab lives at the PLM's own domain with the first label replaced by the lab's
 * name — `qa.findsourcing.com` → `rangeplan.findsourcing.com`, `app.myenv.resourced.ai` →
 * `rangeplan.myenv.resourced.ai`. That is what lets one bundle serve QA and every preview
 * environment without knowing which one it is.
 *
 * Production is the exception, and deliberately so: it is served from more than one registrable
 * domain, and `findsourcing.com` carries a production PLM while the *QA* labs live there too.
 * Deriving from the hostname would send a production user on findsourcing.com to QA's Range Plan —
 * logged in, since the cookie is scoped to findsourcing.com, and with no sign anything was wrong.
 * So the production build uses the urls baked into `.env.production` and never derives.
 *
 * Keyed on the build mode rather than on whether a value happens to be set, so adding a sixth lab
 * and forgetting it in `.env.production` yields a missing link rather than a link to QA.
 */

export type LabName = 'rangeplan' | 'canvas' | 'planalytics' | 'labels' | 'sessions' | 'workspace';

/**
 * Domains that carry one preview environment per subdomain, each environment owning a label of its
 * own: `app.myenv.resourced.ai`, `rangeplan.myenv.resourced.ai`. A host sitting directly on one of
 * these belongs to no environment, so it has no labs to point at.
 *
 * Kept in step with `Auth:Cookie:NestedRootDomains` in Api/appsettings.json, which splits the same
 * hosts the same way for the session cookie.
 */
const PREVIEW_ROOT_DOMAINS = ['resourced.ai'];

const bakedUrls: Record<LabName, string | undefined> = {
  rangeplan: import.meta.env.VITE_RANGEPLAN_URL,
  canvas: import.meta.env.VITE_IMAGEGEN_URL,
  planalytics: import.meta.env.VITE_ANALYTICS_URL,
  labels: import.meta.env.VITE_LABELS_URL,
  sessions: import.meta.env.VITE_SESSIONS_URL,
  workspace: import.meta.env.VITE_WORKSPACE_URL,
};

/**
 * The lab's url for the host this page is being served from, or undefined when this environment
 * has no labs — in which case the caller should render no link rather than a dead one.
 */
export function labUrl(lab: LabName): string | undefined {
  if (import.meta.env.MODE === 'production') return bakedUrls[lab];
  // Outside production a baked url is a local override, set in `.env.development.local` to point
  // at a lab running somewhere the hostname rule cannot reach, such as a Lovable preview.
  return bakedUrls[lab] ?? deriveLabUrl(lab, window.location.hostname);
}

/** Exported for tests: the hostname rule on its own, with no build-mode or browser dependency. */
export function deriveLabUrl(lab: string, hostname: string): string | undefined {
  const labels = hostname.split('.');

  // localhost, or a bare registrable domain: nothing to put a lab name in front of.
  if (labels.length < 3) return undefined;

  const parent = labels.slice(1).join('.');

  // Directly on the preview root, so there is no environment label to hang a lab off: the link
  // would resolve to the shared root, where no lab lives.
  if (PREVIEW_ROOT_DOMAINS.includes(parent)) return undefined;

  return `https://${lab}.${parent}`;
}
